import React, { useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
} from "recharts";
import { Card, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";

const stripFts = (str) => {
  if (typeof str !== "string") return str || "";
  return str.replace(/<\/?(?:fts_match|span)[^>]*>/gi, "");
};

const CustomScatterTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;

  return (
    <div className="bg-white/95 backdrop-blur-md p-3 rounded-lg border border-gray-200 shadow-lg text-xs max-w-xs z-[99999]">
      <p className="font-bold text-gray-900 text-sm mb-0.5 line-clamp-1">{data.name}</p>
      {data.subInfo && <p className="text-gray-500 mb-2 line-clamp-1">{data.subInfo}</p>}
      <div className="space-y-1 pt-1 border-t border-gray-100">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">{data.xLabel}:</span>
          <span className="font-semibold text-gray-800">{Number(data.x).toLocaleString()}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">{data.yLabel}:</span>
          <span className="font-semibold text-gray-800">{Number(data.y).toLocaleString()}</span>
        </div>
        {data.zValue !== undefined && (
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">{data.zLabel}:</span>
            <span className="font-semibold text-gray-800">{Number(data.zValue).toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CollectionGraphView = ({ items = [], entityType = "researchers" }) => {
  const chartData = useMemo(() => {
    return items.map((item) => {
      let x = 0;
      let y = 0;
      let z = 10;
      let xLabel = "Citations";
      let yLabel = "H-Index";
      let zLabel = "Publications";
      let subInfo = "";

      if (entityType === "units") {
        x = Number(item.total_citation ?? item.citations ?? 0);
        y = Number(item.total_h_index ?? item.hIndex ?? 0);
        z = Number(item.total_publication ?? item.publications ?? 10);
        xLabel = "Total Citations";
        yLabel = "Total H-Index";
        zLabel = "Total Publications";
        subInfo = item.institution_name || "";
      } else if (entityType === "institutions") {
        x = Number(item.total_citation ?? item.citations ?? 0);
        y = Number(item.total_faculties ?? item.faculties ?? 0);
        z = Number(item.total_h_index ?? item.hIndex ?? 10);
        xLabel = "Total Citations";
        yLabel = "Total Faculties";
        zLabel = "H-Index";
        subInfo = [item.city, item.state].filter(Boolean).join(", ");
      } else if (entityType === "venues") {
        x = Number(item.citation_count ?? item.citations ?? 0);
        y = Number(item.h_index ?? item.hIndex ?? 0);
        z = Number(item.publication_count ?? item.publications ?? 10);
        xLabel = "Citations";
        yLabel = "H-Index";
        zLabel = "Publications";
        subInfo = Array.isArray(item.keywords) ? item.keywords.slice(0, 2).join(", ") : "Venue";
      } else {
        // researchers
        x = Number(item.citation_count ?? item.citations ?? 0);
        y = Number(item.h_index ?? item.hIndex ?? 0);
        z = Number(item.publication_count ?? item.publications ?? 10);
        xLabel = "Citations";
        yLabel = "H-Index";
        zLabel = "Publications";
        subInfo = item.institution_name || item.position || "";
      }

      return {
        id: item.id,
        name: stripFts(item.name),
        subInfo: stripFts(subInfo),
        x,
        y,
        z,
        zValue: z,
        xLabel,
        yLabel,
        zLabel,
      };
    });
  }, [items, entityType]);

  const stats = useMemo(() => {
    if (!chartData.length) return { count: 0, avgX: 0, avgY: 0, maxX: 0, maxY: 0 };
    const count = chartData.length;
    const totalX = chartData.reduce((acc, d) => acc + d.x, 0);
    const totalY = chartData.reduce((acc, d) => acc + d.y, 0);
    const maxX = Math.max(...chartData.map((d) => d.x));
    const maxY = Math.max(...chartData.map((d) => d.y));

    return {
      count,
      avgX: Math.round(totalX / count),
      avgY: Math.round(totalY / count),
      maxX,
      maxY,
      xLabel: chartData[0]?.xLabel || "X",
      yLabel: chartData[0]?.yLabel || "Y",
    };
  }, [chartData]);

  if (!items.length) {
    return (
      <div className="py-12 text-center text-gray-500 text-sm">
        No data available to plot graph.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Metric summary banner */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-explore/5 rounded-lg border border-explore/20 text-left">
          <span className="text-[11px] font-semibold text-explore uppercase tracking-wide block">
            Cohort Size
          </span>
          <span className="text-lg font-bold text-gray-900">{stats.count}</span>
          <span className="text-[11px] text-gray-500 block">entities plotted</span>
        </div>

        <div className="p-3 bg-slate-50 rounded-lg border border-gray-200 text-left">
          <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block">
            Avg {stats.yLabel}
          </span>
          <span className="text-lg font-bold text-gray-900">{stats.avgY.toLocaleString()}</span>
          <span className="text-[11px] text-gray-500 block">Peak: {stats.maxY.toLocaleString()}</span>
        </div>

        <div className="p-3 bg-slate-50 rounded-lg border border-gray-200 text-left">
          <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block">
            Avg {stats.xLabel}
          </span>
          <span className="text-lg font-bold text-gray-900">{stats.avgX.toLocaleString()}</span>
          <span className="text-[11px] text-gray-500 block">Peak: {stats.maxX.toLocaleString()}</span>
        </div>
      </div>

      {/* Scatter distribution graph */}
      <Card className="border-gray-200 shadow-xs bg-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 px-2">
            <div>
              <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                Distribution & Correlation View
              </h4>
              <p className="text-[11px] text-gray-500">
                Hover over data points to inspect individual entity details.
              </p>
            </div>
            <Badge variant="outline" className="text-[10px] text-gray-500">
              {stats.xLabel} vs {stats.yLabel}
            </Badge>
          </div>

          <div className="h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 30, bottom: 25, left: 15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  type="number"
                  dataKey="x"
                  name={stats.xLabel}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val)}
                  label={{
                    value: stats.xLabel,
                    position: "insideBottom",
                    offset: -15,
                    style: { fontSize: 11, fill: "#64748b", fontWeight: 500 },
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name={stats.yLabel}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val)}
                  label={{
                    value: stats.yLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: 5,
                    style: { fontSize: 11, fill: "#64748b", fontWeight: 500 },
                  }}
                />
                <ZAxis type="number" dataKey="z" range={[60, 260]} />
                <Tooltip content={<CustomScatterTooltip />} cursor={{ strokeDasharray: "3 3" }} />
                <Scatter
                  name="Entities"
                  data={chartData}
                  fill="#0f3460"
                  fillOpacity={0.75}
                  stroke="#0a2342"
                  strokeWidth={1}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CollectionGraphView;
